import { Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { useCallback } from "react";

import { Col, Row } from "@/components/customComponents";
import { TAG_DISPLAY_LIMIT } from "@/pages/CreatorAnalytics/constants";
import type { ICharacterPerformance } from "@/pages/CreatorAnalytics/types";
import { formatNumber, formatRatingPercent, getNetLikesColor, getRatingColor } from "@/pages/CreatorAnalytics/utils";
import { cn } from "@/utils/cn";

import CharacterAvatar from "./CharacterAvatar";

const TD_BASE = "px-4 py-3";

interface ICharacterRowProps {
  character: ICharacterPerformance;
  rank: number;
  isClickable: boolean;
  onSelect?: (character: ICharacterPerformance) => void;
}

const CharacterRow = ({ character, rank, isClickable, onSelect }: ICharacterRowProps) => {
  const handleClick = useCallback(() => {
    onSelect?.(character);
  }, [onSelect, character]);

  return (
    <tr
      onClick={handleClick}
      className={cn(
        "border-b border-default-100 transition-colors",
        isClickable ? "cursor-pointer hover:bg-primary/5 dark:hover:bg-primary/10" : "hover:bg-default-50 dark:hover:bg-default-50/30",
      )}
    >
      <td className={cn(TD_BASE, "text-default-400")}>{rank}</td>
      <td className={TD_BASE}>
        <Row className="items-center gap-3">
          <CharacterAvatar characterId={character.characterId} name={character.name} avatarUrl={character.avatarUrl} isNsfw={character.isNsfw} />
          <Col className="gap-0.5">
            <Row className="items-center gap-2">
              <Text variant={TextVariants.LABEL_SMALL_BOLD}>{character.name}</Text>
              {character.isNsfw && <span className="rounded bg-red-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-red-400">NSFW</span>}
            </Row>
            <Row className="flex-wrap gap-1">
              {character.tags.slice(0, TAG_DISPLAY_LIMIT).map((tag, tagIdx) => (
                <span key={`${tag}-${tagIdx}`} className="rounded bg-default-100 px-1.5 py-0.5 text-[10px] text-default-500 dark:bg-default-50">
                  {tag}
                </span>
              ))}
              {character.tags.length > TAG_DISPLAY_LIMIT && (
                <span className="text-[10px] text-default-400">+{character.tags.length - TAG_DISPLAY_LIMIT}</span>
              )}
            </Row>
          </Col>
        </Row>
      </td>
      <td className={cn(TD_BASE, "text-right font-medium")}>{formatNumber(character.totalRequests)}</td>
      <td className={cn(TD_BASE, "hidden text-right sm:table-cell")}>{formatNumber(character.uniqueUsers)}</td>
      <td className={cn(TD_BASE, "hidden text-right md:table-cell")}>{formatNumber(character.uniqueConversations)}</td>
      <td className={cn(TD_BASE, "hidden text-right sm:table-cell")}>{character.avgDepth.toFixed(1)}</td>
      <td className={cn(TD_BASE, "text-right")}>
        <span className={getRatingColor(character.avgRating)}>{formatRatingPercent(character.avgRating)}</span>
      </td>
      <td className={cn(TD_BASE, "hidden text-right sm:table-cell")}>
        <span className={getNetLikesColor(character.netLikes)}>
          {character.netLikes > 0 ? "+" : ""}
          {formatNumber(character.netLikes)}
        </span>
      </td>
    </tr>
  );
};

export default CharacterRow;
